/**
 * TanStack Query hook for teacher live-session dashboard.
 * Composite payload from GET /api/teacher/classes/:cid/sessions/:sid/dashboard
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

// ---------------------------------------------------------------------------
// Types — mirror Phase 02 API response shape
// ---------------------------------------------------------------------------
export interface DashboardLesson {
  id: string;
  classId: string;
  orderNo: number;
  title: string;
  description?: string | null;
  scheduledDate?: string | null;
  template?: DashboardCourseLesson | null;
}

export interface DashboardCourseLesson {
  id: string;
  title: string;
  description?: string | null;
  content?: string | null;
  learningObjectives?: string | null;
  teacherNotes?: string | null;
  homeworkSummary?: string | null;
}

export interface DashboardMaterial {
  id: string;
  type: 'file' | 'link' | 'video' | 'document' | 'audio' | 'slide';
  title: string;
  url?: string | null;
  fileKey?: string | null;
  description?: string | null;
  sortOrder: number;
}

export interface DashboardSessionReview {
  enrollmentId: string;
  participation?: number | null;
  comprehension?: number | null;
  behavior?: number | null;
  comment?: string | null;
  reviewedAt?: string | null;
}

export interface DashboardEnrollment {
  enrollmentId: string;
  studentId?: string;
  studentName: string;
  studentCode?: string | null;
  attendanceStatus?: 'present' | 'absent' | 'late' | 'excused' | null;
  attendanceRecordId?: string | null;
  sessionReview?: DashboardSessionReview | null;
}

export interface DashboardAssignment {
  id: string;
  title: string;
  type: string;
  maxScore?: number | null;
  dueDate?: string | null;
  status: 'draft' | 'published' | 'closed';
  submissionCount: number;
  gradedCount: number;
}

export interface DashboardSession {
  id: string;
  sessionDate: string;
  status: 'open' | 'closed';
  teacherNote?: string | null;
  lessonId?: string | null;
  sessionNumber?: number | null;
}

export interface SessionDashboardPayload {
  session: DashboardSession;
  lesson?: DashboardLesson | null;
  materials: DashboardMaterial[];
  enrollments: DashboardEnrollment[];
  assignments: DashboardAssignment[];
  className?: string | null;
  classCode?: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const sessionDashboardKeys = {
  all: ['session-dashboard'] as const,
  bySession: (classId: string, sessionId: string) =>
    ['session-dashboard', classId, sessionId] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useSessionDashboard(classId: string, sessionId: string) {
  return useQuery({
    queryKey: sessionDashboardKeys.bySession(classId, sessionId),
    queryFn: () =>
      api.get<SessionDashboardPayload>(
        `/api/teacher/classes/${classId}/sessions/${sessionId}/dashboard`,
      ),
    enabled: !!classId && !!sessionId,
    staleTime: 30_000,
  });
}
